import { ServiceBanner_productizedService$key } from '#app/__generated__/relay/ServiceBanner_productizedService.graphql.js';
import { Button } from '#app/components/Buttons/Button.js';
import { Text } from '#app/components/Primitives/Text.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useCopyToClipboard } from '#app/hooks/useCopyToClipboard.js';
import { useIsLargeScreen } from '#app/hooks/useIsLargeScreen.js';
import { useLocation } from '#app/hooks/useLocation.js';
import { useNavigation } from '#app/hooks/useNavigation.js';
import { localStorage } from '#app/services/storage.js';
import serviceBackgroundLarge from './service-background-large.webp';
import serviceBackgroundSmall from './service-background-small.webp';
import {
  ServiceEditBannerTitleMultiLine,
  ServiceEditBannerTitleSingleLine,
  ServiceShareBannerTitleMultiLine,
  ServiceShareBannerTitleSingleLine,
} from './ServiceBannerTitle.js';
import { CloseIcon, CopyIcon, EditIcon } from '@contra/icons/icons';
import { graphql, useFragment } from '@contra/relay';
import { useCallback, useState } from 'react';
import { css, styled } from 'styled-components';

const Container = styled.div`
  ${({ theme }) => css`
    width: 100%;
    border-radius: 16px;
    background-color: ${theme.colorsV2.gray20};
    padding: 24px;
    display: flex;
    flex-direction: column;
    position: relative;

    ${theme.mediaQueries.xl} {
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
    }
  `}
`;

const BackgroundImage = styled.div`
  ${({ theme }) => css`
    height: 100%;
    right: 0;
    bottom: 0;
    position: absolute;
    background-image: url(${serviceBackgroundSmall});
    width: 280px;
    border-top-right-radius: 16px;
    border-bottom-right-radius: 16px;

    ${theme.mediaQueries.xl} {
      background-image: url(${serviceBackgroundLarge});
      width: 464px;
    }
  `}
`;

const TextContainer = styled.div`
  position: relative;
`;

const SmallTitleContainer = styled.div`
  ${({ theme }) => css`
    display: block;

    ${theme.mediaQueries.xl} {
      display: none;
    }
  `}
`;

const LargeTitleContainer = styled.div`
  ${({ theme }) => css`
    display: none;

    ${theme.mediaQueries.xl} {
      display: block;
    }
  `}
`;

const SubTitle = styled(Text)`
  margin-top: 8px;
`;

const StyledButton = styled(Button)`
  ${({ theme }) => css`
    margin-top: 24px;
    background-color: ${theme.colorsV2.brandPurple};
    min-width: 144px;
    width: 144px;

    &:hover {
      opacity: 0.9;
    }

    & span {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    ${theme.mediaQueries.xl} {
      margin-top: 0;
    }
  `}
`;

const CloseButton = styled(CloseIcon)`
  cursor: pointer;
  position: absolute;
  top: 16px;
  right: 16px;
`;

const LOCAL_STORAGE_KEY = 'CONTRA_LOCAL_SERVICE_BANNER_HIDE';

type ServiceBannerProps = {
  readonly productizedServiceRef: ServiceBanner_productizedService$key;
};

export const ServiceBanner = ({
  productizedServiceRef,
}: ServiceBannerProps) => {
  const service = useFragment<ServiceBanner_productizedService$key>(
    graphql`
      fragment ServiceBanner_productizedService on ProductizedService {
        updatedAt
        visitorCanEdit
      }
    `,
    productizedServiceRef,
  );
  const { track } = useAnalytics();
  const isLargeScreen = !useIsLargeScreen();
  const { push } = useNavigation();
  const { currentURL, pathname } = useLocation();
  const [hideBanner, setHideBanner] = useState(
    localStorage.getItem(LOCAL_STORAGE_KEY) === 'true',
  );
  const copyToClipboard = useCopyToClipboard();

  const onEdit = useCallback(() => {
    track('button', {
      action: 'tapped',
      button_name: 'demand_service_banner_edit',
    });
    push(`${pathname}/edit`);
  }, [pathname, push, track]);

  const onShare = useCallback(() => {
    track('button', {
      action: 'tapped',
      button_name: 'demand_service_banner_share',
    });

    void copyToClipboard(currentURL);
  }, [copyToClipboard, currentURL, track]);

  const onClose = useCallback(() => {
    track('button', {
      action: 'tapped',
      button_name: 'demand_service_banner_close',
    });
    localStorage.setItem(LOCAL_STORAGE_KEY, 'true');
    setHideBanner(true);
  }, [track]);

  const displayServiceBanner = !hideBanner && service.visitorCanEdit;

  if (!displayServiceBanner) {
    return null;
  }

  // For services created/updated before the rollout of the service rework project,
  // show a banner encouraging the user to update their service. Otherwise,
  // show a banner encouraging the user to share their service.
  if (new Date(service.updatedAt) < new Date('2023-12-14T00:00:00.000Z')) {
    return (
      <Container>
        <BackgroundImage />

        <CloseButton
          onClick={onClose}
          role="button"
          size={isLargeScreen ? 16 : 24}
        />

        <TextContainer>
          <SmallTitleContainer>
            <ServiceEditBannerTitleMultiLine />
          </SmallTitleContainer>
          <LargeTitleContainer>
            <ServiceEditBannerTitleSingleLine />
          </LargeTitleContainer>

          <SubTitle textStyle="bodyRegular">
            Explore new additions like cover images, FAQs, search tags, Quick
            Hire option, and more.
          </SubTitle>
        </TextContainer>

        <StyledButton
          icon={() => <EditIcon size={16} />}
          onClick={onEdit}
          size="small"
          variant="V2Primary"
        >
          Edit service
        </StyledButton>
      </Container>
    );
  }

  return (
    <Container>
      <BackgroundImage />

      <CloseButton
        onClick={onClose}
        role="button"
        size={isLargeScreen ? 16 : 24}
      />

      <TextContainer>
        <SmallTitleContainer>
          <ServiceShareBannerTitleMultiLine />
        </SmallTitleContainer>
        <LargeTitleContainer>
          <ServiceShareBannerTitleSingleLine />
        </LargeTitleContainer>

        <SubTitle textStyle="bodyRegular">
          Share your service on social media to attract clients.
        </SubTitle>
      </TextContainer>

      <StyledButton
        icon={() => <CopyIcon size={16} />}
        onClick={onShare}
        size="small"
        variant="V2Primary"
      >
        Copy link
      </StyledButton>
    </Container>
  );
};
